import React, { useEffect, useRef, useState } from 'react';
import moment from 'moment';
import { withTranslation, WithTranslation, useTranslation } from 'react-i18next';
import { useSpring, useChain, animated, useSpringRef } from 'react-spring';
import { Message } from 'hooks/useVideoSession/useVideoSession';

require('./ChatHistory.css');

// How many seconds to wait for a read receipt before we presume the message to be lost
const READ_RECEIPT_WAIT = 5;

export type Props = {
    history: ReadonlyArray<Message>;
};

export const ChatMessage = ({ text, local, timestamp, acknowledged }: Message) => {
    const { t } = useTranslation();

    const chatBubbleSpringRef = useSpringRef();
    const chatBubbleProps = useSpring({
        ref: chatBubbleSpringRef,
        config: { mass: 1, tension: 400, friction: 26 },
        from: { opacity: .5, transform: 'scale(0.01, 0.01)' },
        to: { opacity: 1, transform: 'scale(1, 1)' },
    });

    const textSpringRef = useSpringRef();
    const textProps = useSpring({
        ref: textSpringRef,
        delay: 100,
        config: { mass: 1, tension: 400, friction: 26 },
        from: { opacity: 0 },
        to: { opacity: 1 },
    });

    const timeSpringRef = useSpringRef();
    const timeProps = useSpring({
        ref: timeSpringRef,
        delay: 200,
        from: { opacity: 0, transform: 'translate(0,-0.5em)' },
        to: { opacity: 1, transform: 'translate(0,0)' },
    });

    useChain([
        chatBubbleSpringRef,
        textSpringRef,
        timeSpringRef,
    ], [ 0, 0, 0 ]);

    const [ presumedLost, setPresumedLost ] = useState(!acknowledged && Date.now() - timestamp >= READ_RECEIPT_WAIT * 1000);

    const timerRef = useRef<NodeJS.Timeout>();
    useEffect(() => {
        if (local) {
            if (!acknowledged && !presumedLost) {
                const timeLeft = timestamp + READ_RECEIPT_WAIT * 1000 - Date.now();
                if (timeLeft <= 0) {
                    setPresumedLost(true);
                }
                else {
                    timerRef.current = setTimeout(() => setPresumedLost(true), timeLeft);
                    return () => {
                        clearTimeout(timerRef.current!);
                    };
                }
            }
            else if (acknowledged) {
                setPresumedLost(false);
            }
        }
    }, [ acknowledged, timestamp, local, presumedLost, setPresumedLost ]);

    return (
        <div className={ `chat-message ${local ? 'local' : 'remote'} ${acknowledged ? 'acknowledged' : ''}` }>
            <animated.div
                className="chat-message-bubble"
                style={ chatBubbleProps }
            >
                <animated.div
                    className="chat-message-text"
                    style={ textProps }
                >
                    { presumedLost
                        ? <s>{ text }</s>
                        : text
                    }
                </animated.div>
            </animated.div>
            <animated.div
                className="chat-message-timestamp"
                style={ timeProps }
            >
                <span>
                    { moment(timestamp).format('LTS') }
                </span>
                { local && !presumedLost &&
                    <svg
                        className={ `checkmark ${acknowledged ? 'visible' : ''}` }
                        viewBox="0 0 110 110"
                    >
                        <path d="M15,50 l-11,11 L40,107 L110,11 l-12,-10 L40,81Z" fill="green"/>
                        <title>
                            { t(acknowledged ? "VIDEO.MESSAGE_RECEIVED" : "VIDEO.MESSAGE_IN_TRANSIT") }
                        </title>
                    </svg>
                }
                { local && presumedLost &&
                    <svg
                        className="presumed-lost"
                        viewBox="0 0 100 100"
                    >
                        <g>
                            <path d="M 6.3895625,6.4195626 C 93.580437,93.610437 93.580437,93.610437 93.580437,93.610437"/>
                            <path d="M 6.3894001,93.6106 C 93.830213,6.4194003 93.830213,6.4194003 93.830213,6.4194003"/>
                            <title>
                                { t("VIDEO.MESSAGE_NOT_ACKNOWLEDGED") }
                            </title>
                        </g>
                    </svg>
                }
            </animated.div>
        </div>
    );
};

// eslint-disable-next-line @typescript-eslint/naming-convention -- TODO: rewrite as a functional component
class _ChatHistory extends React.Component<Props & WithTranslation> {
    chatHistoryDiv: HTMLDivElement | null = null;

    componentDidUpdate() {
        // Scroll down to the latest message
        if (this.chatHistoryDiv) {
            this.chatHistoryDiv.scrollTop = this.chatHistoryDiv.scrollHeight;
        }
    }

    setRef = (e: HTMLDivElement) => {
        this.chatHistoryDiv = e;
    };

    render() {
        const { history, t } = this.props;
        return (
            <div
                id="chat-history"
                ref={ this.setRef }
            >
                { history.length === 0 &&
                    <div className="no-messages">
                        { t('VIDEO.NO_MESSAGES') }
                    </div>
                }
                { history.map(({ messageId, text, local, timestamp, acknowledged }, i) =>
                    <ChatMessage
                        key={ i }
                        messageId={ messageId }
                        text={ text }
                        local={ local }
                        timestamp={ timestamp }
                        acknowledged={ acknowledged }
                    />
                ) }
            </div>
        );
    }
}

export const ChatHistory = withTranslation()(_ChatHistory);